import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = {
  pt: {
    checking: "Confirmando seu pagamento...",
    ok_t: "Pagamento confirmado!",
    ok_s: "Seus créditos foram registrados. No lançamento do app eles entram automaticamente na sua carteira.",
    fail_t: "Pagamento não confirmado",
    fail_s: "Se o valor foi debitado, ele será estornado automaticamente. Tente novamente.",
    back: "Voltar ao início",
  },
  en: {
    checking: "Confirming your payment...",
    ok_t: "Payment confirmed!",
    ok_s: "Your credits are registered. When the app launches they will appear in your wallet automatically.",
    fail_t: "Payment not confirmed",
    fail_s: "If the amount was charged, it will be refunded automatically. Please try again.",
    back: "Back to home",
  },
  es: {
    checking: "Confirmando tu pago...",
    ok_t: "¡Pago confirmado!",
    ok_s: "Tus créditos quedaron registrados. Cuando la app se lance, aparecerán automáticamente en tu billetera.",
    fail_t: "Pago no confirmado",
    fail_s: "Si se debitó el valor, será reembolsado automáticamente. Inténtalo de nuevo.",
    back: "Volver al inicio",
  },
};

export default function PaymentSuccess() {
  const { lang } = useLang();
  const t = L[lang];
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const tries = useRef(0);
  const fired = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("fail");
      return;
    }
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          if (!fired.current) {
            fired.current = true;
            confetti({ particleCount: 160, spread: 90, origin: { y: 0.6 }, colors: ["#38bdf8", "#2e6bff", "#fbbf24", "#ffffff"] });
          }
          return;
        }
      } catch (_) {}
      tries.current += 1;
      if (tries.current < 12) setTimeout(poll, 2000);
      else setState("fail");
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-[#06091a] text-white flex items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 20 }}
        className="max-w-md w-full rounded-3xl border border-white/10 bg-[#0c1122]/90 backdrop-blur p-10 text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-300 animate-spin mx-auto mb-5" />
            <p data-testid="payment-checking" className="font-display font-bold text-lg">{t.checking}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="grid place-items-center w-16 h-16 rounded-full bg-emerald-400/15 border border-emerald-400/40 mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-300" />
            </span>
            <h1 data-testid="payment-ok" className="font-display font-black text-2xl mb-3">{t.ok_t}</h1>
            <p className="text-sm text-slate-400 leading-relaxed">{t.ok_s}</p>
          </>
        )}
        {state === "fail" && (
          <>
            <span className="grid place-items-center w-16 h-16 rounded-full bg-red-400/15 border border-red-400/40 mx-auto mb-5">
              <XCircle className="w-8 h-8 text-red-300" />
            </span>
            <h1 data-testid="payment-fail" className="font-display font-black text-2xl mb-3">{t.fail_t}</h1>
            <p className="text-sm text-slate-400 leading-relaxed">{t.fail_s}</p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-back-btn"
          className="inline-block mt-8 rounded-full bg-amber-400 text-black font-bold text-sm px-7 py-3 transition-all duration-200 hover:bg-amber-300 hover:scale-105 active:scale-95"
        >
          {t.back}
        </Link>
      </motion.div>
    </div>
  );
}
